/**
 * ModalAutorizacaoCancelamento Component
 * 
 * Modal para autorização de cancelamento de NFC-e.
 * Requer credenciais de um usuário autorizado e justificativa.
 * 
 * Funcionalidades:
 * - Validação de credenciais em tempo real
 * - Justificativa com mínimo de 15 caracteres
 * - Feedback visual de erros
 * - Loading state durante processamento
 */

import { useState, useEffect } from 'react'
import { X, AlertCircle, Lock, User, FileText, Loader } from 'lucide-react'
import './ModalAutorizacaoCancelamento.css'

function ModalAutorizacaoCancelamento({ 
  isOpen, 
  onClose, 
  onConfirm, 
  venda 
}) {
  const [formData, setFormData] = useState({
    usuario: '',
    senha: '',
    justificativa: ''
  })
  
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [apiError, setApiError] = useState(null)

  // Limpar formulário ao abrir/fechar
  useEffect(() => {
    if (isOpen) {
      setFormData({ usuario: '', senha: '', justificativa: '' })
      setErrors({})
      setApiError(null)
    }
  }, [isOpen])

  const validateField = (name, value) => {
    const newErrors = { ...errors }

    switch (name) {
      case 'usuario':
        if (!value.trim()) {
          newErrors.usuario = 'Usuário é obrigatório'
        } else {
          delete newErrors.usuario
        }
        break

      case 'senha':
        if (!value) {
          newErrors.senha = 'Senha é obrigatória'
        } else if (value.length < 4) {
          newErrors.senha = 'Senha muito curta'
        } else {
          delete newErrors.senha
        }
        break

      case 'justificativa':
        if (!value.trim()) {
          newErrors.justificativa = 'Justificativa é obrigatória'
        } else if (value.trim().length < 15) {
          newErrors.justificativa = `Mínimo de 15 caracteres (${value.trim().length}/15)`
        } else {
          delete newErrors.justificativa
        }
        break

      default:
        break
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    validateField(name, value)
    setApiError(null) // Limpar erro da API ao digitar
  }

  const isFormValid = () => {
    return (
      formData.usuario.trim() &&
      formData.senha.length >= 4 &&
      formData.justificativa.trim().length >= 15 &&
      Object.keys(errors).length === 0
    )
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validar todos os campos
    const usuarioValid = validateField('usuario', formData.usuario)
    const senhaValid = validateField('senha', formData.senha)
    const justificativaValid = validateField('justificativa', formData.justificativa)

    if (!usuarioValid || !senhaValid || !justificativaValid) {
      return
    }

    try {
      setLoading(true)
      setApiError(null)
      
      await onConfirm(formData)
      
      // Sucesso - o modal será fechado pelo componente pai
    } catch (err) {
      setApiError(
        err.response?.data?.message || 
        'Erro ao cancelar NFC-e. Verifique suas credenciais.'
      )
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="modal-auth-overlay" onClick={onClose}>
      <div 
        className="modal-auth-content" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-auth-header">
          <div className="modal-auth-icon-wrapper">
            <Lock size={24} />
          </div>
          <div>
            <h3 className="modal-auth-title">Autorização de Cancelamento</h3>
            <p className="modal-auth-subtitle">
              Venda #{venda?.id} - R$ {parseFloat(venda?.total_amount || 0).toFixed(2)}
            </p>
          </div>
          <button 
            className="modal-auth-close" 
            onClick={onClose}
            disabled={loading}
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="modal-auth-body">
          {/* Mensagem de aviso */}
          <div className="auth-warning">
            <AlertCircle size={18} />
            <div>
              <strong>Atenção!</strong>
              <p>
                Para cancelar esta NFC-e, é necessário autorização de um 
                usuário com permissão para cancelar vendas. O estoque será automaticamente 
                revertido após o cancelamento.
              </p>
            </div>
          </div>

          {/* Erro da API */}
          {apiError && (
            <div className="auth-error">
              <AlertCircle size={18} />
              {apiError}
            </div>
          )}

          {/* Campo: Usuário */}
          <div className="form-group">
            <label htmlFor="usuario" className="form-label">
              <User size={16} />
              Usuário Autorizador
            </label>
            <input
              type="text"
              id="usuario"
              name="usuario"
              value={formData.usuario}
              onChange={handleChange}
              disabled={loading}
              placeholder="Digite o usuário com permissão"
              className={`form-input ${errors.usuario ? 'input-error' : ''}`}
              autoComplete="username"
            />
            {errors.usuario && (
              <span className="form-error">{errors.usuario}</span>
            )}
          </div>

          {/* Campo: Senha */}
          <div className="form-group">
            <label htmlFor="senha" className="form-label">
              <Lock size={16} />
              Senha
            </label>
            <input
              type="password"
              id="senha"
              name="senha"
              value={formData.senha}
              onChange={handleChange}
              disabled={loading}
              placeholder="Digite a senha"
              className={`form-input ${errors.senha ? 'input-error' : ''}`}
              autoComplete="current-password"
            />
            {errors.senha && (
              <span className="form-error">{errors.senha}</span>
            )}
          </div>

          {/* Campo: Justificativa */}
          <div className="form-group">
            <label htmlFor="justificativa" className="form-label">
              <FileText size={16} />
              Justificativa do Cancelamento
            </label>
            <textarea
              id="justificativa"
              name="justificativa"
              value={formData.justificativa}
              onChange={handleChange}
              disabled={loading}
              placeholder="Descreva o motivo do cancelamento (mínimo 15 caracteres)"
              className={`form-textarea ${errors.justificativa ? 'input-error' : ''}`}
              rows="4"
            />
            <div className="form-helper">
              <span className={errors.justificativa ? 'text-error' : 'text-muted'}>
                {errors.justificativa || `${formData.justificativa.trim().length}/15 caracteres`}
              </span>
            </div>
          </div>

          {/* Informações da Chave */}
          {venda?.chave_nfe && (
            <div className="info-box">
              <strong>Chave de Acesso:</strong>
              <code>{venda.chave_nfe}</code>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="modal-auth-footer">
          <button
            type="button"
            className="btn-cancel"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn-confirm"
            onClick={handleSubmit}
            disabled={!isFormValid() || loading}
          >
            {loading ? (
              <>
                <Loader size={16} className="spinning" />
                Processando...
              </>
            ) : (
              <>
                <Lock size={16} />
                Confirmar Cancelamento
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ModalAutorizacaoCancelamento

